// Packages and components
import React, { useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDFooter from '@ui/Footer/SHDFooter';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDImage from '@ui/Image/SHDImage';
import SHDModalContainer from '@ui/ModalContainer/SHDModalContainer';
import SHDSelectable from '@ui/Selectable/SHDSelectable';
import SHDText from '@ui/Text/SHDText';

// Helpers and utilities
import { MEVO_SINGLE, MEVO_MULTI } from '@constants/strings';
import { usePointCapture } from '@hooks/useCapture';

// Types and interfaces
import { CaptureLogoUrl } from './../camera-selection.d';
import type { MevoModalProps } from '../schedule.d';

const MevoModal: React.FC<MevoModalProps> = ({
  appHost,
  appHostService,
  capture,
  onClose,
}) => {
  // States
  const [selectedMevoType, setSelectedMevoType] = useState<
    null | typeof MEVO_SINGLE | typeof MEVO_MULTI
  >(null);

  const [isClicked, setIsClicked] = useState<boolean>(false);

  // Hooks
  const { updateMevoType } = usePointCapture();

  return (
    <>
      <SHDModalContainer gap="small">
        <Box gap="xsmall" margin={{ bottom: 'large' }}>
          <SHDHeading level={3} formatCasing={false}>
            Select your Mevo type
          </SHDHeading>
        </Box>
        <Box align="start" gap="xxsmall">
          <SHDSelectable
            id="mevo-single"
            isActive={selectedMevoType === MEVO_SINGLE}
            onSelected={() => setSelectedMevoType(MEVO_SINGLE)}
            align="start"
          >
            <Box direction="row" justify="start" align="center">
              <SHDImage
                src={CaptureLogoUrl.MEVO_SINGLE}
                alt="Logo for MEVO Single"
                height={48}
                width={48}
              />
              <Box margin={{ left: 'medium' }}>
                <SHDText style="subhead-bold" color="black">
                  {MEVO_SINGLE}
                </SHDText>
              </Box>
            </Box>
          </SHDSelectable>

          <SHDSelectable
            id="mevo-multi"
            isActive={selectedMevoType === MEVO_MULTI}
            onSelected={() => setSelectedMevoType(MEVO_MULTI)}
            align="start"
          >
            <Box direction="row" justify="start" align="center">
              <SHDImage
                src={CaptureLogoUrl.MEVO_MULTI}
                alt="Logo for MEVO Multi"
                height={48}
                width={48}
              />
              <Box margin={{ left: 'medium' }}>
                <SHDText style="subhead-bold" color="black">
                  {MEVO_MULTI}
                </SHDText>
              </Box>
            </Box>
          </SHDSelectable>
        </Box>
      </SHDModalContainer>
      <SHDFooter>
        <SHDModalContainer gap="xxsmall">
          <SHDButton
            primary
            label="Select Mevo"
            disabled={selectedMevoType === null || isClicked}
            busy={isClicked}
            onClick={() => {
              if (selectedMevoType !== null) {
                setIsClicked(true);
                updateMevoType(capture, selectedMevoType).then(() => {
                  const url: string =
                    selectedMevoType === MEVO_SINGLE
                      ? (capture.rtmp?.mevoAppUrl as string)
                      : (capture.rtmp?.mevoMulticamUrl as string);
                  if (appHost.isHosted) {
                    appHostService?.notifications.openAppLink(url);
                  } else {
                    window.location.href = url;
                  }
                  onClose();
                });
              }
            }}
          />
        </SHDModalContainer>
      </SHDFooter>
    </>
  );
};
export default MevoModal;
